import { Button, Radio, RadioGroup } from "@9amhealth/shared";
import { FC, useEffect, useState } from "react";
import Translate from "../Translate/Translate";
import { useBloc } from "@blac/react";
import { ShippingAddressSuggestionCubit } from "src/state/ShippingAddress/ShippingAddressSuggestionCubit";
import { ShippingAddressData } from "src/state/ShippingAddress/ShippingAddressData";
import {
  type ShippingAddressSuggestionOption,
  ShippingFormAddressSuggestionStyled
} from "./ShippingFormAddressSuggestion.styled";

const {
  formatAddress,
  TilesContainer,
  Tile,
  TileAddress,
  TileContent,
  TileLabel,
  ActionsContainer,
  SuggestionDialog
} = ShippingFormAddressSuggestionStyled;

interface ShippingFormAddressSuggestionProps {
  cubitId: string;
  onSave?: (address: ShippingAddressData) => void;
  onEdit?: () => void;
  onClose?: () => void;
}

export const ShippingFormAddressSuggestion: FC<
  ShippingFormAddressSuggestionProps
> = ({ cubitId, onSave, onEdit, onClose }) => {
  const [
    { suggestion, originalAddress, isSuggestionActive },
    { updateShippingAddressSuggestion }
  ] = useBloc(ShippingAddressSuggestionCubit, { id: cubitId });

  const [selected, setSelected] =
    useState<ShippingAddressSuggestionOption>("suggested");

  useEffect(() => {
    setSelected(suggestion?.differsFromFallback ? "suggested" : "original");
  }, [suggestion?.differsFromFallback]);

  if (!(originalAddress && suggestion)) {
    return null;
  }

  const handleSave = (): void => {
    const address =
      selected === "suggested" ? suggestion.suggestedAddress : originalAddress;
    onSave?.(address);
    updateShippingAddressSuggestion({ isSuggestionActive: false });
  };

  const handleClose = () => {
    onClose?.();
    updateShippingAddressSuggestion({ isSuggestionActive: false });
  };

  const handleEdit = () => {
    onEdit?.();
    updateShippingAddressSuggestion({ isSuggestionActive: false });
  };

  return (
    <SuggestionDialog
      isOpen={isSuggestionActive}
      onClose={handleClose}
      title={<Translate msg="shippingAddressSuggestion.title" />}
      actions={
        <ActionsContainer>
          <Button theme="sunrise" onPress={handleSave}>
            <Translate msg="shippingAddressSuggestion.save" />
          </Button>
          <Button theme="simple" hideArrow onPress={handleEdit}>
            <Translate msg="shippingAddressSuggestion.edit" />
          </Button>
        </ActionsContainer>
      }
    >
      <Translate
        msg={
          suggestion.differsFromFallback
            ? "shippingAddressSuggestion.description"
            : "shippingAddressSuggestion.noSuggestionDescription"
        }
      />
      <RadioGroup
        defaultValue={selected}
        value={selected}
        onChange={(value) =>
          setSelected(value as ShippingAddressSuggestionOption)
        }
      >
        <TilesContainer>
          {suggestion.differsFromFallback && (
            <Tile
              data-is-selected={selected === "suggested" ? "" : undefined}
              onClick={() => setSelected("suggested")}
            >
              <Radio
                value={"suggested"}
                aria-label={"Suggested address radio"}
              />
              <TileContent>
                <TileLabel>
                  <Translate msg="shippingAddressSuggestion.suggestedAddress" />
                </TileLabel>
                <TileAddress>
                  {formatAddress(suggestion.suggestedAddress)
                    .split("\n")
                    .map((line, i) => (
                      <span key={i}>
                        {line}
                        {i === 0 && <br />}
                      </span>
                    ))}
                </TileAddress>
              </TileContent>
            </Tile>
          )}

          <Tile
            data-is-selected={selected === "original" ? "" : undefined}
            onClick={() => setSelected("original")}
          >
            <Radio value={"original"} aria-label={"Original address radio"} />
            <TileContent>
              <TileLabel>
                <Translate msg="shippingAddressSuggestion.originalAddress" />
              </TileLabel>
              <TileAddress>
                {formatAddress(originalAddress)
                  .split("\n")
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </TileAddress>
            </TileContent>
          </Tile>
        </TilesContainer>
      </RadioGroup>
    </SuggestionDialog>
  );
};
